import { useInvoiceRenderContext } from '../api/use-invoice-render-context';
import { invoiceFieldDocPath, invoiceFieldsFor } from '../field-model/invoice-field-model';

import type { InvoiceKindKey } from '../field-model/invoice-field-types';
import type { InvoiceRow } from '../types';

// The document fields the editor does not collect as inputs — the
// override-persisted set edited at Review — shown with the value the document
// would render right now (override if set, else the system resolution from
// the render-context read). This keeps every field that lands on the document
// visible in the editor instead of hidden behind the review step; a failed or
// pending read degrades to em-dashes, never to a missing section.
export function ResolvedDocumentSection({
  invoice,
  kind,
}: Readonly<{ invoice: InvoiceRow | null; kind: InvoiceKindKey }>) {
  const query = useInvoiceRenderContext(invoice?.id ?? null);
  const fields = invoiceFieldsFor(kind).filter((def) => def.storage === 'override');
  if (invoice === null || fields.length === 0) return null;
  const values = query.data?.values ?? {};
  const overrides = query.data?.overrides ?? {};
  return (
    <section
      aria-labelledby="resolved-document-heading"
      className="space-y-2 rounded-md border p-3"
      data-testid="resolved-document-section"
    >
      <h4 id="resolved-document-heading" className="text-sm font-semibold">
        Document values
      </h4>
      <p className="text-muted-foreground text-xs">
        What the issued document will print for the review-owned fields. Confirm or override them in
        the Review step before issuing.
      </p>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {fields.map((def) => {
          const path = invoiceFieldDocPath(def) ?? def.key;
          const effective = overrides[def.key] ?? values[path];
          return (
            <div key={def.key} className="flex min-w-0 flex-col">
              <dt className="text-muted-foreground text-xs">{def.label}</dt>
              <dd className="truncate">{displayValue(effective)}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

function displayValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  if (!text.includes('<')) return text === '' ? '—' : text;
  // The resolved VAT note arrives as an HTML block; a tag dump is noise in a
  // summary row, so parse the markup down to its text.
  const parsed = new DOMParser().parseFromString(text, 'text/html');
  const plain = parsed.body.textContent.replace(/\s+/g, ' ').trim();
  return plain === '' ? '—' : plain;
}
